import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { HOMEPAGE_URL } from '../config/urls';

interface AuthPageLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: React.ReactNode;
  footer?: React.ReactNode;
  /** When true, omits the large 6Tool logo above the title (partner connect shows logos in the card). */
  hideTopLogo?: boolean;
}

export default function AuthPageLayout({
  children,
  title,
  subtitle,
  footer,
  hideTopLogo = false,
}: AuthPageLayoutProps) {
  const { theme } = useTheme();

  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden'>
      <motion.div
        className='absolute -top-40 -right-40 w-80 h-80 bg-indigo-200 dark:bg-indigo-900 rounded-full opacity-20 blur-3xl pointer-events-none'
        initial={{ x: 0, y: 0, scale: 1 }}
        animate={{ x: 100, y: 50, scale: 1.1 }}
        transition={{ duration: 2, ease: 'easeOut' }}
      />
      <motion.div
        className='absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200 dark:bg-blue-900 rounded-full opacity-20 blur-3xl pointer-events-none'
        initial={{ x: 0, y: 0, scale: 1 }}
        animate={{ x: -100, y: -50, scale: 1.1 }}
        transition={{ duration: 2.5, ease: 'easeOut' }}
      />

      <div className='max-w-md w-full space-y-8 relative z-10'>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className='text-center'
        >
          {!hideTopLogo ? (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className='flex justify-center mb-6'
            >
              <a
                href={HOMEPAGE_URL}
                className='relative cursor-pointer block hover:opacity-80 transition-opacity'
                title='Return to homepage'
              >
                <motion.img
                  src={
                    theme === 'light'
                      ? '/img/6tool-logo-light-background.svg'
                      : '/img/6tool-logo.svg'
                  }
                  alt='6Tool Logo'
                  className='h-20 w-auto mx-auto'
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/img/logo.png';
                    target.onerror = () => {
                      target.style.display = 'none';
                    };
                  }}
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
                />
              </a>
            </motion.div>
          ) : null}
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className='text-4xl font-bold bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-400 dark:to-blue-400 bg-clip-text text-transparent'
          >
            {title}
          </motion.h1>
          {subtitle ? (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className='mt-2 text-sm text-gray-600 dark:text-gray-400'
            >
              {subtitle}
            </motion.p>
          ) : null}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className='bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 backdrop-blur-sm border border-gray-200 dark:border-gray-700'
        >
          {children}
        </motion.div>

        {footer}
      </div>
    </div>
  );
}
